import type { FieldProtocol } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { BatchImportFileActionConfig, FlowNode } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, isEmptyVariableValue, isRichTextValue, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList, useDataSourceOptions } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { textRichVariableName } from '../util'

type BatchImportFileCardProps = {
    config: BatchImportFileActionConfig
}

const SectionLabel = styled.div`
    font-size: 12px;
    color: var(--color-gray-600);
    line-height: 20px;
    margin: 4px 0;
`

const BatchImportFileCard: React.FC<BatchImportFileCardProps> = ({ config }) => {
    const { dataSourceId, attachmentFieldId, fileSource, importMode } = config
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dsOptions = useDataSourceOptions()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const userOption = useGetUserRecordOptions()

    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const dsOption = useMemo(
        () => (dataSourceId ? dsOptions.find(item => item.value === dataSourceId) : undefined),
        [dataSourceId, dsOptions]
    )
    const schema = useMemo(() => dataSourceList.find(ds => ds.id === dataSourceId)?.schema || {}, [dataSourceId, dataSourceList])

    const attachmentFieldName = useMemo(() => {
        if (!attachmentFieldId) {
            return ''
        }
        const record = schema as Record<string, FieldProtocol>
        const field = record?.[attachmentFieldId]
        return field?.name || attachmentFieldId
    }, [attachmentFieldId, schema])

    const importModeLabel = useMemo(() => {
        switch (importMode) {
            case 'IMAGES':
                return '直接导入'
            case 'ARCHIVE':
                return '解压导入'
            default:
                return ''
        }
    }, [importMode])

    const nodeId = useMemo(() => {
        if (fileSource?.type === VariableType.UPSTREAM) {
            return fileSource?.upstreamVariable?.nodeId || ''
        }
        if (fileSource?.type === VariableType.ARG_VARIABLE) {
            return fileSource?.argVariable?.nodeId || ''
        }
        return ''
    }, [fileSource])

    const nodes = useNodesData([nodeId]) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    const fileSourceText = useMemo(() => {
        if (fileSource?.type === VariableType.VALUE) {
            const result: any = fileSource?.valueVariable?.value || ''
            if (typeof result === 'string') {
                return result
            }
            if (isRichTextValue(result)) {
                return ''
            }
            if (typeof result === 'object') {
                return Array.isArray(result) ? result.join(',') : JSON.stringify(result)
            }
            return typeof result === 'string' ? result : JSON.stringify(result)
        }
        return (
            textRichVariableName({
                userOption,
                upstreamOptions,
                data: fileSource
            }) || ''
        )
    }, [fileSource, upstreamOptions, userOption])

    return (
        <CM.CardItemContainer>
            <CM.FieldList>
                {(dataSourceId || attachmentFieldId) && <SectionLabel>目标导入字段</SectionLabel>}
                {dataSourceId && (
                    <CM.FieldItem>
                        <CM.FieldItemTitle>数据表</CM.FieldItemTitle>
                        <CM.FieldItemTitle color="var(--color-gray-900)">{dsOption?.label || '找不到数据源'}</CM.FieldItemTitle>
                    </CM.FieldItem>
                )}
                {attachmentFieldId && (
                    <CM.FieldItem>
                        <CM.FieldItemTitle>附件字段</CM.FieldItemTitle>
                        <CM.FieldItemTitle color="var(--color-gray-900)">{attachmentFieldName || '找不到字段'}</CM.FieldItemTitle>
                    </CM.FieldItem>
                )}
                {fileSource && !isEmptyVariableValue(fileSource) && (
                    <>
                        <SectionLabel style={{ marginTop: 8 }}>导入的文件</SectionLabel>
                        <CM.FieldItemContent>
                            <CM.FieldItemTitle
                                whiteSpace="pre-wrap"
                                color={fileSource?.type === VariableType.VALUE ? undefined : 'var(--color-purple-500)'}
                            >
                                {fileSourceText || '找不到变量'}
                            </CM.FieldItemTitle>
                        </CM.FieldItemContent>
                    </>
                )}
                {importMode && (
                    <>
                        <SectionLabel style={{ marginTop: 8 }}>导入设置</SectionLabel>
                        <CM.FieldItem>
                            <CM.FieldItemTitle>当导入压缩包时</CM.FieldItemTitle>
                            <CM.FieldItemTitle color="var(--color-gray-900)">{importModeLabel || '未设置'}</CM.FieldItemTitle>
                        </CM.FieldItem>
                    </>
                )}
            </CM.FieldList>
        </CM.CardItemContainer>
    )
}

export default BatchImportFileCard
